import { useUser } from "@dashboard/auth/useUser";
import { payoutListUrl } from "@dashboard/payouts/urls";
import { sellerReturnsListUrl } from "@dashboard/sellerReturns/urls";
import { reviewsModerationListUrl } from "@dashboard/reviewsModeration/urls";
import { rfqListUrl } from "@dashboard/rfq/urls";
import { sellerListUrl } from "@dashboard/sellers/urls";
import { pricingRulesListUrl } from "@dashboard/pricingRules/urls";
import { attributeListUrl } from "@dashboard/attributes/urls";
import { brandListUrl } from "@dashboard/brands/urls";
import { categoryListUrl } from "@dashboard/categories/urls";
import { channelsListUrl } from "@dashboard/channels/urls";
import { warehouseListUrl } from "@dashboard/warehouses/urls";
import { shippingZonesListUrl } from "@dashboard/shipping/urls";
import { collectionListUrl } from "@dashboard/collections/urls";
import { iconSize } from "@dashboard/components/icons";
import { configurationMenuUrl } from "@dashboard/configuration/urls";
import { getConfigMenuItemsPermissions } from "@dashboard/configuration/utils";
import {
  customerCartWishlistActivityUrl,
  customerListB2bUrl,
  customerListB2cUrl,
} from "@dashboard/customers/urls";
import {
  applicationListUrl,
  bankListUrl,
  creditOverviewUrl,
} from "@dashboard/creditFinance/urls";
import { reportsOverviewUrl } from "@dashboard/reports/urls";
import { saleListUrl, voucherListUrl } from "@dashboard/discounts/urls";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import { ExtensionsPaths } from "@dashboard/extensions/urls";
import { giftCardListUrl } from "@dashboard/giftCards/urls";
import { productTypeListUrl } from "@dashboard/productTypes/urls";
import { PermissionEnum } from "@dashboard/graphql";
import { ConfigurationIcon } from "@dashboard/icons/Configuration";
import { CustomersIcon } from "@dashboard/icons/Customers";
import { DiscountsIcon } from "@dashboard/icons/Discounts";
import { HomeIcon } from "@dashboard/icons/Home";
import { OrdersIcon } from "@dashboard/icons/Orders";
import { ProductsIcon } from "@dashboard/icons/Products";
import { commonMessages, sectionNames } from "@dashboard/intl";
import { pageListPath } from "@dashboard/modeling/urls";
import {
  orderDraftListUrl,
  orderListUrl,
  orderReturnRequestsPath,
} from "@dashboard/orders/urls";
import { refundTransactionsPath } from "@dashboard/refundTransactions/urls";
import { productListUrl } from "@dashboard/products/urls";
import { menuListUrl } from "@dashboard/structures/urls";
import { taxConfigurationListUrl } from "@dashboard/taxes/urls";
import { languageListUrl } from "@dashboard/translations/urls";
import { Box } from "@saleor/macaw-ui-next";
import Taxes from "@dashboard/icons/Taxes";
import { Archive, BarChart3, Building2, CreditCard, MapPin, Store, Tag, Wallet } from "lucide-react";
import { useIntl } from "react-intl";

import { type SidebarMenuItem } from "../types";
import { mapToExtensionsItems } from "../utils";

export function useMenuStructure() {
  const extensions = useExtensions(extensionMountPoints.NAVIGATION_SIDEBAR);
  const intl = useIntl();
  const { user } = useUser();

  const appExtensionsHeaderItem: SidebarMenuItem = {
    id: "extensions",
    label: intl.formatMessage(sectionNames.appExtensions),
    type: "divider",
    paddingY: 1.5,
  };

  const menuItems: SidebarMenuItem[] = [
    {
      icon: renderIcon(<HomeIcon />),
      iconColor: "#4F46E5",
      label: intl.formatMessage(sectionNames.home),
      id: "home",
      url: "/",
      type: "item",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.products),
          id: "products",
          url: productListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.categories),
          id: "categories",
          url: categoryListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.collections),
          id: "collections",
          url: collectionListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.brands),
          id: "brands",
          url: brandListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: "Reviews",
          id: "reviews-moderation",
          url: reviewsModerationListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.giftCards),
          id: "giftCards",
          url: giftCardListUrl(),
          permissions: [PermissionEnum.MANAGE_GIFT_CARD],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.attributes),
          id: "attributes",
          url: attributeListUrl(),
          permissions: [PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.productTypes),
          id: "productTypes",
          url: productTypeListUrl(),
          permissions: [PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES],
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_CATALOG, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<ProductsIcon />),
      iconColor: "#8B5CF6",
      url: productListUrl(),
      label: intl.formatMessage(sectionNames.catalog),
      permissions: [PermissionEnum.MANAGE_GIFT_CARD, PermissionEnum.MANAGE_PRODUCTS, PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES],
      id: "products",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.orders),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "orders",
          url: orderListUrl(),
          type: "item",
        },
        {
          label: "In Cart",
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "order-drafts",
          url: orderDraftListUrl(),
          type: "item",
        },
        {
          label: "Quote Requests",
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "rfq",
          url: rfqListUrl(),
          type: "item",
        },
        {
          label: "Return Requests",
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "order-return-requests",
          url: orderReturnRequestsPath,
          type: "item",
        },
        {
          label: "Seller Returns",
          permissions: [PermissionEnum.MANAGE_SELLERS],
          id: "seller-returns",
          url: sellerReturnsListUrl(),
          type: "item",
        },
        {
          label: "Refund",
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "refunds",
          url: refundTransactionsPath,
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_ORDERS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<OrdersIcon />),
      iconColor: "#F59E0B",
      label: intl.formatMessage(sectionNames.fulfillment),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "orders",
      url: orderListUrl(),
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage({
            id: "customers.b2b.nav",
            defaultMessage: "Business Customers",
          }),
          permissions: [PermissionEnum.MANAGE_USERS],
          id: "customers-b2b",
          url: customerListB2bUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage({
            id: "customers.b2c.nav",
            defaultMessage: "Individual Customers",
          }),
          permissions: [PermissionEnum.MANAGE_USERS],
          id: "customers-b2c",
          url: customerListB2cUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage({
            id: "customers.cartWishlistActivity.nav",
            defaultMessage: "Cart & Wishlist Activity",
          }),
          permissions: [PermissionEnum.MANAGE_USERS],
          id: "customers-cart-wishlist-activity",
          url: customerCartWishlistActivityUrl(),
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_CUSTOMERS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<CustomersIcon />),
      iconColor: "#10B981",
      label: intl.formatMessage(sectionNames.customers),
      permissions: [PermissionEnum.MANAGE_USERS],
      id: "customers",
      url: customerListB2bUrl(),
      type: "itemGroup",
    },
    {
      icon: renderIcon(<CreditCard size={iconSize.small} strokeWidth={2.4} />),
      iconColor: "#0EA5E9",
      label: intl.formatMessage({ id: "credit.nav", defaultMessage: "Credit" }),
      permissions: [PermissionEnum.MANAGE_USERS],
      id: "credit",
      url: creditOverviewUrl,
      type: "itemGroup",
      children: [
        {
          label: intl.formatMessage({
            id: "credit.overview.nav",
            defaultMessage: "Overview",
          }),
          permissions: [PermissionEnum.MANAGE_USERS],
          id: "credit-overview",
          url: creditOverviewUrl,
          type: "item",
        },
        {
          label: intl.formatMessage({ id: "credit.banks.nav", defaultMessage: "Banks" }),
          permissions: [PermissionEnum.MANAGE_USERS],
          id: "credit-banks",
          url: bankListUrl,
          type: "item",
        },
        {
          label: intl.formatMessage({
            id: "credit.applications.nav",
            defaultMessage: "Applications",
          }),
          permissions: [PermissionEnum.MANAGE_USERS],
          id: "credit-applications",
          url: applicationListUrl,
          type: "item",
        },
      ],
    },
    {
      icon: renderIcon(<Store size={iconSize.small} strokeWidth={2.4} />),
      iconColor: "#EF4444",
      label: intl.formatMessage(sectionNames.sellers),
      permissions: [PermissionEnum.MANAGE_SELLERS],
      id: "sellers",
      url: sellerListUrl(),
      type: "item",
    },
    {
      icon: renderIcon(<Wallet size={iconSize.small} strokeWidth={2.4} />),
      iconColor: "#059669",
      label: "Payouts",
      permissions: [PermissionEnum.MANAGE_SELLERS],
      id: "payouts",
      url: payoutListUrl(),
      type: "item",
    },
    {
      icon: renderIcon(<BarChart3 size={iconSize.small} strokeWidth={2.4} />),
      iconColor: "#7C3AED",
      label: intl.formatMessage({ id: "reports.nav", defaultMessage: "Reports" }),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "reports",
      url: reportsOverviewUrl,
      type: "item",
    },
    {
      icon: renderIcon(<MapPin size={iconSize.small} strokeWidth={2} />),
      iconColor: "#3B82F6",
      label: "Locations",
      permissions: [PermissionEnum.MANAGE_CHANNELS, PermissionEnum.MANAGE_SHIPPING, PermissionEnum.MANAGE_PRODUCTS],
      id: "locations",
      url: channelsListUrl(),
      type: "itemGroup",
      children: [
        {
          label: intl.formatMessage(sectionNames.channels),
          id: "channels",
          url: channelsListUrl(),
          permissions: [PermissionEnum.MANAGE_CHANNELS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.warehouses),
          id: "warehouses",
          url: warehouseListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.shippingZones),
          id: "shipping-zones",
          url: shippingZonesListUrl(),
          permissions: [PermissionEnum.MANAGE_SHIPPING],
          type: "item",
        },
      ],
    },
    {
      icon: renderIcon(<Tag size={iconSize.small} strokeWidth={2} />),
      iconColor: "#14B8A6",
      label: intl.formatMessage(sectionNames.pricingRules),
      permissions: [PermissionEnum.MANAGE_PRODUCTS],
      id: "pricing-rules",
      url: pricingRulesListUrl(),
      type: "item",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.vouchers),
          id: "vouchers",
          url: voucherListUrl(),
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_DISCOUNTS, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<DiscountsIcon />),
      iconColor: "#EC4899",
      label: intl.formatMessage(commonMessages.discounts),
      permissions: [PermissionEnum.MANAGE_DISCOUNTS],
      url: voucherListUrl(),
      id: "discounts",
      type: "itemGroup",
    },
    {
      icon: renderIcon(<Taxes />),
      iconColor: "#F97316",
      label: intl.formatMessage(sectionNames.taxes),
      permissions: [PermissionEnum.MANAGE_TAXES],
      id: "taxes",
      url: taxConfigurationListUrl(),
      type: "item",
    },
    {
      icon: renderIcon(<ConfigurationIcon />),
      iconColor: "#64748B",
      label: intl.formatMessage(sectionNames.configuration),
      permissions: getConfigMenuItemsPermissions(intl),
      id: "configure",
      url: configurationMenuUrl,
      type: "item",
    },
    {
      icon: renderIcon(<Archive size={iconSize.small} strokeWidth={2} />),
      iconColor: "#94A3B8",
      label: "Unused",
      permissions: [],
      id: "unused",
      url: ExtensionsPaths.installedExtensions,
      type: "itemGroup",
      children: [
        {
          label: intl.formatMessage(sectionNames.extensions),
          id: "unused-extensions",
          url: ExtensionsPaths.installedExtensions,
          permissions: [],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.promotions),
          id: "unused-promotions",
          url: saleListUrl(),
          permissions: [PermissionEnum.MANAGE_DISCOUNTS],
          type: "item",
        },
      ],
    },
  ];
  const isMenuItemPermitted = (menuItem: SidebarMenuItem) => {
    const userPermissions = (user?.userPermissions || []).map(permission => permission.code);

    if (!menuItem?.permissions || menuItem?.permissions?.length < 1) {
      return true;
    }

    return menuItem.permissions.some(permission => userPermissions.includes(permission));
  };
  const getFilteredMenuItems = (menuItems: SidebarMenuItem[]) =>
    menuItems.filter(isMenuItemPermitted);

  return menuItems.reduce((resultItems: SidebarMenuItem[], menuItem: SidebarMenuItem) => {
    if (!isMenuItemPermitted(menuItem)) {
      return resultItems;
    }

    const { children } = menuItem;
    const filteredChildren = children ? getFilteredMenuItems(children) : undefined;

    return [...resultItems, { ...menuItem, children: filteredChildren }];
  }, []);
}

function renderIcon(icon: React.ReactNode) {
  return (
    <Box __width={20} __height={20}>
      {icon}
    </Box>
  );
}
